<template>
  <div class="h-full flex flex-col bg-gray-950">

    <!-- Tab bar -->
    <div class="flex items-center bg-gray-800 border-b border-gray-700 shrink-0 min-h-[2rem] overflow-x-auto">
      <button
        v-for="tab in terminalStore.tabs"
        :key="tab.id"
        class="flex items-center gap-1.5 px-3 py-1 text-xs border-r border-gray-700 whitespace-nowrap transition-colors shrink-0"
        :class="tab.id === terminalStore.activeTab
          ? 'bg-gray-900 text-white'
          : 'text-gray-400 hover:text-white hover:bg-gray-750'"
        @click="terminalStore.activeTab = tab.id"
      >
        {{ tab.name }}
        <span
          class="ml-0.5 text-gray-500 hover:text-red-400 leading-none"
          title="Close terminal"
          @click.stop="closeTab(tab.id)"
        >×</span>
      </button>

      <button
        class="px-2.5 py-1 text-xs text-gray-500 hover:text-white shrink-0 transition-colors"
        title="Open new terminal"
        @click="openNew"
      >＋</button>
    </div>

    <!-- Terminal containers — all kept mounted simultaneously so each xterm.js
         instance retains its scroll buffer across tab switches.
         'invisible' (visibility:hidden) is used instead of 'hidden' (display:none)
         so inactive containers keep their layout dimensions: xterm.js needs a
         non-zero measured size to fit correctly. -->
    <div class="flex-1 relative min-h-0">
      <div
        v-for="tab in terminalStore.tabs"
        :key="tab.id"
        :ref="el => mountTerminal(el as HTMLElement | null, tab.id, tab.cwd)"
        class="absolute inset-0 p-1"
        :class="tab.id === terminalStore.activeTab ? '' : 'invisible'"
      />
    </div>

  </div>
</template>

<script setup lang="ts">
import { onUnmounted } from 'vue'
import { useTerminalStore } from '@/stores/terminalStore'
import { useRepoStore }     from '@/stores/repoStore'
import { createTerminal, disposeTerminal } from './terminalManager'

const terminalStore = useTerminalStore()
const repoStore     = useRepoStore()

// Tracks which session IDs have already been mounted so v-ref callbacks that
// fire on every re-render don't call createTerminal a second time for the
// same element.
const mounted = new Set<string>()

/**
 * Called by Vue's v-ref callback for each tab container.
 * Creates the xterm.js terminal and opens the server PTY on first call only.
 */
function mountTerminal(el: HTMLElement | null, id: string, cwd: string): void {
  if (!el || mounted.has(id)) return
  mounted.add(id)
  createTerminal(el, id, cwd)
}

/**
 * Open a new terminal tab in the active repository root.
 * activeRepoPath returns the absolute server path (repo.path ?? repo.name),
 * which is what the server's isSafeCwd() validates against ALLOWED_REPO_PATHS.
 */
function openNew(): void {
  const cwd = repoStore.activeRepoPath || '/'
  terminalStore.openTerminal(cwd)
}

/**
 * Close a single terminal tab: dispose the client terminal and server PTY,
 * then remove the tab from the store.
 */
function closeTab(id: string): void {
  disposeTerminal(id)
  terminalStore.closeTab(id)
  mounted.delete(id)
}

/**
 * TerminalPanel uses v-if in App.vue, so onUnmounted fires when the panel is
 * hidden. Dispose all terminals here so server PTYs are killed and session IDs
 * are freed. When the panel reopens, createTerminal will re-spawn fresh PTYs
 * for any tabs still in the store.
 */
onUnmounted(() => {
  for (const id of mounted) {
    disposeTerminal(id)
  }
  mounted.clear()
})
</script>
